import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/OAuthContext";
import AuthService from "../../services/AuthService";
import useSignIn from "../sign-in/useSignIn";
import routes from "../../constants/Routes";
import GLOBAL from "../../global";
import AfterSSOSignUp from "../sign-up/AfterSSOSignUp";
import { setEventData } from "../../features/askEmma/askEmmaSlice";
import { useDispatch } from "react-redux";
import AppContext from "../../contexts/AppContext";
import AppLoading from "../shared/loading/AppLoading";
import storageService from "../../utils/StorageService";
import CONSTANT from "../../constants/Constant";
import AlertModal from "../shared/AlertModal";
import { createWebRTCToken } from "../shared/MovementModal/Services/AssessementService";
import { toast } from "react-toastify";
import { useTenantFeatures } from "../../features/tenantFeatures/useTenantFeature";

const Callback = () => {
    const { storeUserInfo, storeUserInfoToLocalStorage, authenticate } = useContext(AppContext);
    const [needsDemographicUpdate, setNeedsDemographicUpdate] = useState(false);
    const [ssoRegistrationData, setSsoRegistrationData] = useState(null);
    const [tempSSOUserData, setTempSSOUserData] = useState(null);
    const [showSubscriptionError, setShowSubscriptionError] = useState(false);
    const [inviattionGId, setInviattionGId] = useState('');
    const [showSSOError, setShowSSOError] = useState(false);
    const [ssoErrorMessage, setSsoErrorMessage] = useState('');

    const location = useLocation();
    const { getToken, getProfileUsingToken } = useAuth();
    const { handleSignInResponse } = useSignIn();
    const history = useHistory();
    const dispatch = useDispatch();
    const processingRef = React.useRef(false);
    const { isAthleteEnabled } = useTenantFeatures();

    useEffect(() => {
        // Prevent multiple executions
        if (!processingRef.current) {
            processingRef.current = true;
            handleSSO();
        }
    }, []);

    useEffect(() => {
        const url = window.location.href;
        const params = new URLSearchParams(url.slice(url.indexOf('?')));
        const invGId = params.get('InvGId') || params.get('invgid');

        if (invGId && params.get('RA') === 'true') {
            setInviattionGId(invGId)
        }
    }, []);

    const handleSSOError = (error) => {
        setSsoErrorMessage(error || 'An error occurred during SSO verification.');
        setShowSSOError(true);
    };

    const onSubmitUpdateSSOUserInformation = async (formValues, setIsSubmitting) => {
        setIsSubmitting(true);

        const finalPayload = {
            Height: +formValues.height_feet * 12 + +formValues.height_inch,
            Weight: formValues.Weight,
            Gender: formValues.Gender,
            DominantHand: formValues.DominantHand,
            DateOfBirth: new Date(formValues.dateOfBirth),
        };

        GLOBAL.USER_INFO.Height = finalPayload.Height;
        GLOBAL.USER_INFO.Weight = finalPayload.Weight;
        GLOBAL.USER_INFO.Gender = finalPayload.Gender;
        GLOBAL.USER_INFO.DominantHand = finalPayload.DominantHand;
        GLOBAL.USER_INFO.DateofBirth = finalPayload.DateofBirth;

        const response = await AuthService.updateSSOUserInformation(finalPayload);
        const { data, success } = response;

        if (!success) {
            setIsSubmitting(false);
            return;
        }

        setIsSubmitting(false);

        dispatch(setEventData({ eventName: 'patientPortal_RegistrationSuccess' }));

        const payload = {
            FirstName: tempSSOUserData?.FirstName,
            LastName: tempSSOUserData?.LastName,
            Weight: tempSSOUserData?.Weight,
            TimeZone: "Eastern Standard Time",
            Gender: tempSSOUserData?.Gender || "N/A",
            DominantHand: tempSSOUserData?.DominantHand,
            EmailAddress: tempSSOUserData?.EmailAddress,
            Height: tempSSOUserData?.Height,
            SSOUserId: tempSSOUserData?.SSOUserId,
            SubscriptionType: parseInt(localStorage.getItem('SubscriptionType'), 10),
        };
        const verifySSOResponse = await AuthService.verifySSO(payload);
        const { data: ssoUserData, success: verifySSOSuccess, error } = verifySSOResponse;
        if (!verifySSOSuccess) {
            handleSSOError(error);
            return;
        }

        await handleSignInResponse(ssoUserData);

        if (inviattionGId) {
            let webRTCCreateTokenResponse = await createWebRTCToken(inviattionGId);
            if (webRTCCreateTokenResponse?.success) {
                const webRTCPortalUrl = webRTCCreateTokenResponse?.data?.WrtcUrl || '';
                const token = webRTCCreateTokenResponse?.data?.Token || '';
                const url = window.location.href;
                const params = new URLSearchParams(url.slice(url.indexOf('?')));
                const qs = [`roomId=${token}`];
                if (params.get('TestId')) qs.push(`TestId=${params.get('TestId')}`);
                if (params.get('RA')) qs.push(`RA=${params.get('RA')}`);
                if (params.get('BodyRegionIds')) qs.push(`BodyRegionIds=${params.get('BodyRegionIds')}`);

                window.location.href = `${webRTCPortalUrl}/?roomId=${token}`;
            } else {
                toast.error(webRTCCreateTokenResponse?.error);
            }

            return;
        } else {
            if (isAthleteEnabled) {
                window.location.href = `${routes.myAssessments}/false`;
            } else {
                window.location.href = routes.dashboard;
            }
        }
    };

    const handleSSO = async () => {
        const queryParams = new URLSearchParams(location.search);
        const code = queryParams.get("code");
        const state = queryParams.get("state");
        const callBackSource = queryParams.get("callBackSource");

        if (code && state) {
            const token = await getToken(code);
            if (token) {
                const data = await getProfileUsingToken();
                if (data) {
                    // set in global user info variable
                    GLOBAL.USER_INFO.SUBSCRIPTION_TYPE = data.subscriptionType;
                    localStorage.setItem('SubscriptionType', data.subscriptionType === undefined || data.subscriptionType === null ? '0' : data.subscriptionType);

                    const baseAppData = {
                        FirstName: data?.firstName,
                        LastName: data?.lastName,
                        TimeZone: "Eastern Standard Time",
                        EmailAddress: data?.email,
                        SSOUserId: data?.id,
                        DateofBirth: data?.dateOfBirth,
                    };
                    const subscriptionType = parseInt(localStorage.getItem('SubscriptionType'), 10);
                    if (callBackSource === 'IOS') {
                        window.webkit.messageHandlers.authHandler.postMessage({
                            ...baseAppData,
                            SubscriptionType: subscriptionType,
                            Tenant: GLOBAL.TENANT,
                        });
                    } else if (callBackSource === 'Android') {
                        const androidDataStr = JSON.stringify({
                            ...baseAppData,
                            SubscriptionType: subscriptionType,
                            Tenant: GLOBAL.TENANT,
                        });

                        window.AndroidAuthHandler.postMessage(androidDataStr);
                    } else {
                        // // Check if subscription type is greater than 0
                        // if (subscriptionType < 1 || subscriptionType > 9) {
                        //     setShowSubscriptionError(true);
                        //     // Redirect to login after 4 seconds
                        //     // setTimeout(() => {
                        //     //     history.push(routes.landing);
                        //     // }, 4000);
                        //     return;
                        // }

                        const payload = {
                            ...baseAppData,
                            Weight: data?.weightLbs,
                            Gender: data?.gender || "N/A",
                            DominantHand: data?.dominantHand,
                            Height: data?.heightInches,
                            SubscriptionType: parseInt(localStorage.getItem('SubscriptionType'), 10),
                        };

                        const response = await AuthService.verifySSO(payload);
                        const { data: ssoUserData, success, error } = response;
                        if (!success) {
                            handleSSOError(error);
                            return;
                        }

                        storageService.removeItem(CONSTANT.JWT.TOKEN_KEY);
                        storageService.removeItem(CONSTANT.REFRESH_TOKEN.KEY_NAME);

                        storageService.addItem(CONSTANT.JWT.TOKEN_KEY, ssoUserData?.Token);
                        storageService.addItem(CONSTANT.REFRESH_TOKEN.KEY_NAME, ssoUserData?.RefreshToken);

                        // const isDemographicIncomplete =
                        //     ssoUserData?.Height <= 0 ||
                        //     ssoUserData?.Weight <= 0 ||
                        //     !ssoUserData?.Gender ||
                        //     !ssoUserData?.DominantHand ||
                        //     !ssoUserData?.DateofBirth;
                        //
                        // if (isDemographicIncomplete) {
                        //     setNeedsDemographicUpdate(true);
                        //
                        //     setTempSSOUserData(ssoUserData);
                        //     storeUserInfo(GLOBAL.USER_INFO);
                        //
                        //     storeUserInfoToLocalStorage();
                        // } else {
                        //     Object.assign(GLOBAL.USER_INFO, {
                        //         Height: ssoUserData?.Height,
                        //         Weight: ssoUserData?.Weight,
                        //         Gender: ssoUserData?.Gender,
                        //         DominantHand: ssoUserData?.DominantHand,
                        //         DateofBirth: ssoUserData?.DateofBirth,
                        //     });
                        //
                        //     await handleSignInResponse(ssoUserData);
                        //
                        //     const isAthleteEnabled = (GLOBAL.TENANT_FEATURES?.find(f => f.Name === 'IsAthleteEnabled')?.Value || 'False') === 'True';
                        //     if (isAthleteEnabled) {
                        //         history.push(`${routes.myAssessments}/false`);
                        //     } else {
                        //         history.push(routes.dashboard);
                        //     }
                        // }

                        Object.assign(GLOBAL.USER_INFO, {
                            Height: ssoUserData?.Height,
                            Weight: ssoUserData?.Weight,
                            Gender: ssoUserData?.Gender,
                            DominantHand: ssoUserData?.DominantHand,
                            DateofBirth: ssoUserData?.DateofBirth,
                        });

                        await handleSignInResponse(ssoUserData);

                        // Clean up URL parameters to prevent reuse of authorization code
                        window.history.replaceState({}, document.title, window.location.pathname);

                        // Clean up OAuth state from localStorage
                        localStorage.removeItem('oauth_state');
                        localStorage.removeItem('code_verifier');

                        // Use window.location.href instead of history.push to force a full page reload
                        // This ensures the authentication state is properly loaded from localStorage
                        // and prevents race conditions with React state updates
                        if (isAthleteEnabled) {
                            window.location.href = `${routes.myAssessments}/false`;
                        } else {
                            window.location.href = routes.dashboard;
                        }
                    }
                }
            }
        }
    };

    return (
        <div>
            {needsDemographicUpdate ? (
                <AfterSSOSignUp
                    onSubmitUpdateSSOUserInformation={onSubmitUpdateSSOUserInformation}
                    ssoRegistrationData={ssoRegistrationData}
                />
            ) : (
                <div className={"position-center"}>
                    <AppLoading />
                </div>
            )}

            <AlertModal
                show={showSubscriptionError}
                type="warning"
                // title="Access Denied"
                showConfirm={false}
                showCancel={false}
                closeOnClickOutside={false}
            >
                <div>
                    AI Guided Therapy requires a different subscription type. Please contact support@mybackhub.com.
                </div>
            </AlertModal>

            <AlertModal
                show={showSSOError}
                type="error"
                showConfirm={false}
                showCancel={false}
                closeOnClickOutside={false}
            >
                <div>{ssoErrorMessage}</div>
            </AlertModal>
        </div>
    );
};

export default Callback;